// TODO: Move out of `mp/` — provider is page-agnostic.
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  PriceOverrideRecord,
  _2dMapCombinedQuote,
} from '@larki/shared/type/order.v2';
import {
  useGetPriceOverridesQuery,
  useSetMapLockMutation,
} from '../../../redux/rtk';
import { useOnCloseTab } from '../../../hooks/useOnCloseTab';

export const SITE_DEPLOYMENT_FEE_KEY = 'site_deployment_fee';
export const customOrderKey = (orderId: string) => `custom_${orderId}` as const;

function emptyMap<K, V>(prev: Map<K, V>): Map<K, V> {
  return prev.size === 0 ? prev : new Map();
}
function emptySet<T>(prev: Set<T>): Set<T> {
  return prev.size === 0 ? prev : new Set();
}

function extractSubtotalFromQuote(
  quote: _2dMapCombinedQuote,
  product: string
): number | null {
  if (product.startsWith('custom_')) {
    const customId = product.replace('custom_', '');
    const order = quote.customOrders?.find((o) => o.id === customId);
    return order && typeof order.price === 'number' ? order.price : null;
  }
  if (product === SITE_DEPLOYMENT_FEE_KEY) {
    for (const section of [quote.nonRfq, quote.rfq]) {
      for (const fee of section.totals.fees) {
        if (typeof fee.amount === 'number') return fee.amount;
      }
    }
    return null;
  }
  for (const section of [quote.nonRfq, quote.rfq]) {
    for (const sub of Object.values(section.composition)) {
      for (const charge of sub.charges) {
        if (charge.product === product) {
          return typeof charge.subtotal === 'number' ? charge.subtotal : null;
        }
      }
    }
  }
  return null;
}

type BannerState = 'hidden' | 'editing' | 'completed' | 'notified';

type StaffPriceEditContextValue = {
  isEditing: boolean;
  bannerState: BannerState;
  startEditing: () => void;
  cancelEditing: () => void;
  completedEditing: () => void;
  notifiedCustomer: () => void;
  dismissBanner: () => void;
  overrides: Map<string, number | null>;
  setOverride: (product: string, subtotal: number | null) => void;
  hasActiveOverrides: boolean;
  activeOverrides: PriceOverrideRecord[];
  activeOverridesByProduct: Map<string, PriceOverrideRecord>;
  isLoadingOverrides: boolean;
  refetchOverrides: () => void;
  refreshQuote: (
    overrides?: PriceOverrideRecord[],
    newVersionId?: number
  ) => void;
  deleteOverride: (product: string) => void;
  pendingResets: Set<string>;
  markForReset: (product: string) => void;
  resetPreviews: Record<string, number | null>;
  isLoadingResetPreviews: boolean;
};

const StaffPriceEditContext = createContext<StaffPriceEditContextValue | null>(
  null
);

export function useStaffPriceEditContext() {
  const ctx = useContext(StaffPriceEditContext);
  if (!ctx) {
    throw new Error(
      'useStaffPriceEditContext must be used within StaffPriceEditProvider'
    );
  }
  return ctx;
}

export function useOptionalStaffPriceEditContext() {
  return useContext(StaffPriceEditContext);
}

// Inert defaults outside edit mode let callers render unconditionally.
export function useEditablePrice(
  productKey: string | undefined,
  fallbackSubtotal: number | 'TBC'
) {
  const ctx = useOptionalStaffPriceEditContext();
  const isEditing = !!ctx?.isEditing && !!productKey;

  if (!isEditing || !ctx || !productKey) {
    return {
      isEditing: false as const,
      editableValue: null,
      originalValue: null,
      hasActiveOverride: false,
      setOverride: () => {},
    };
  }

  const localOverride = ctx.overrides.get(productKey);
  const activeOverride = ctx.activeOverridesByProduct.get(productKey);
  const fallback =
    typeof fallbackSubtotal === 'number' ? fallbackSubtotal : null;

  return {
    isEditing: true as const,
    editableValue: localOverride !== undefined ? localOverride : fallback,
    originalValue: activeOverride?.originalSubtotal ?? fallback,
    hasActiveOverride: !!activeOverride,
    setOverride: (val: number | null) => ctx.setOverride(productKey, val),
  };
}

export function StaffPriceEditProvider({
  children,
  jobId,
  viewingVersionId,
  asMember,
  onRefreshQuote,
  onEditing,
  previewResetQuote,
  quoteVersion = 0,
}: {
  children: React.ReactNode;
  jobId: number | null;
  viewingVersionId: number | null;
  asMember: boolean;
  onRefreshQuote?: (
    overrides?: PriceOverrideRecord[],
    newVersionId?: number
  ) => void;
  onEditing?: (editing: boolean) => void;
  previewResetQuote?: (
    products: string[],
    asMember: boolean
  ) => Promise<_2dMapCombinedQuote | null>;
  quoteVersion?: number;
}) {
  const [setMapLock] = useSetMapLockMutation();
  const unlockMap = useCallback(() => {
    if (jobId) {
      setMapLock({
        params: { jobId },
        reqBody: { locked: false },
      });
    }
  }, [jobId, setMapLock]);
  const [isEditing, setIsEditing] = useState(false);
  const [bannerState, setBannerState] = useState<BannerState>('hidden');
  const [overrides, setOverrides] = useState<Map<string, number | null>>(
    new Map()
  );
  const [pendingResets, setPendingResets] = useState<Set<string>>(new Set());

  const {
    data: activeOverrides = [],
    isLoading: isLoadingOverrides,
    refetch: refetchOverrides,
  } = useGetPriceOverridesQuery(
    {
      jobId: jobId!,
      ...(viewingVersionId != null && { map2dVersionId: viewingVersionId }),
    },
    { skip: !jobId }
  );

  const activeOverridesByProduct = useMemo(() => {
    const map = new Map<string, PriceOverrideRecord>();
    for (const o of activeOverrides) map.set(o.product, o);
    return map;
  }, [activeOverrides]);

  const hasActiveOverrides = activeOverrides.length > 0;

  const startEditing = useCallback(() => {
    const initial = new Map<string, number | null>();
    for (const override of activeOverrides) {
      initial.set(override.product, override.overrideSubtotal);
    }
    setOverrides(initial);
    setPendingResets(emptySet);
    setIsEditing(true);
    setBannerState('editing');
    if (jobId) {
      setMapLock({
        params: { jobId },
        reqBody: { locked: true, mode: 'price_editing' },
      });
    }
  }, [activeOverrides, jobId, setMapLock]);

  const cancelEditing = useCallback(() => {
    setOverrides(emptyMap);
    setPendingResets(emptySet);
    setIsEditing(false);
    setBannerState('hidden');
    unlockMap();
  }, [unlockMap]);

  const completedEditing = useCallback(() => {
    setOverrides(emptyMap);
    setPendingResets(emptySet);
    setIsEditing(false);
    setBannerState('completed');
    unlockMap();
  }, [unlockMap]);

  const notifiedCustomer = useCallback(() => {
    setBannerState('notified');
  }, []);

  const dismissBanner = useCallback(() => {
    setBannerState('hidden');
  }, []);

  const setOverride = useCallback(
    (product: string, subtotal: number | null) => {
      setOverrides((prev) => {
        if (prev.get(product) === subtotal) return prev;
        const next = new Map(prev);
        next.set(product, subtotal);
        return next;
      });
      setPendingResets((prev) => {
        if (!prev.has(product)) return prev;
        const next = new Set(prev);
        next.delete(product);
        return next;
      });
    },
    []
  );

  const deleteOverride = useCallback((product: string) => {
    setOverrides((prev) => {
      const next = new Map(prev);
      next.delete(product);
      return next;
    });
  }, []);

  const markForReset = useCallback((product: string) => {
    setOverrides((prev) => {
      if (!prev.has(product)) return prev;
      const next = new Map(prev);
      next.delete(product);
      return next;
    });
    setPendingResets((prev) => {
      if (prev.has(product)) return prev;
      const next = new Set(prev);
      next.add(product);
      return next;
    });
  }, []);

  const [resetPreviews, setResetPreviews] = useState<
    Record<string, number | null>
  >({});
  const [isLoadingResetPreviews, setIsLoadingResetPreviews] = useState(false);
  const previewSeqRef = useRef(0);

  useEffect(() => {
    if (pendingResets.size === 0 || !previewResetQuote) {
      setResetPreviews({});
      setIsLoadingResetPreviews(false);
      return;
    }
    const products = Array.from(pendingResets);

    const customPreviews: Record<string, null> = {};
    const engineProducts: string[] = [];
    for (const p of products) {
      if (p.startsWith('custom_')) {
        customPreviews[p] = null;
      } else {
        engineProducts.push(p);
      }
    }

    if (engineProducts.length === 0) {
      setResetPreviews(customPreviews);
      setIsLoadingResetPreviews(false);
      return;
    }

    const seq = ++previewSeqRef.current;
    setIsLoadingResetPreviews(true);
    previewResetQuote(engineProducts, asMember)
      .then((quote) => {
        if (seq !== previewSeqRef.current) return;
        const next: Record<string, number | null> = { ...customPreviews };
        if (quote) {
          for (const product of engineProducts) {
            next[product] = extractSubtotalFromQuote(quote, product);
          }
        }
        setResetPreviews(next);
      })
      .catch(() => {
        if (seq !== previewSeqRef.current) return;
        setResetPreviews(customPreviews);
      })
      .finally(() => {
        if (seq !== previewSeqRef.current) return;
        setIsLoadingResetPreviews(false);
      });
  }, [pendingResets, quoteVersion, asMember, previewResetQuote]);

  const onRefreshQuoteRef = useRef(onRefreshQuote);
  onRefreshQuoteRef.current = onRefreshQuote;

  const refreshTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const refreshQuote = useCallback(
    (overrides?: PriceOverrideRecord[], newVersionId?: number) => {
      // When overrides are passed in (e.g. straight from the savePriceOverrides
      // response) we can refresh synchronously — there is no ref/render race to
      // wait for. Otherwise defer so the parent's RTK-driven re-render commits
      // first.
      if (overrides) {
        onRefreshQuoteRef.current?.(overrides, newVersionId);
        return;
      }
      if (refreshTimeoutRef.current) clearTimeout(refreshTimeoutRef.current);
      refreshTimeoutRef.current = setTimeout(() => {
        refreshTimeoutRef.current = null;
        onRefreshQuoteRef.current?.();
      }, 0);
    },
    []
  );
  useEffect(
    () => () => {
      if (refreshTimeoutRef.current) clearTimeout(refreshTimeoutRef.current);
    },
    []
  );

  const value = useMemo<StaffPriceEditContextValue>(
    () => ({
      isEditing,
      bannerState,
      startEditing,
      cancelEditing,
      completedEditing,
      notifiedCustomer,
      dismissBanner,
      overrides,
      setOverride,
      hasActiveOverrides,
      activeOverrides,
      activeOverridesByProduct,
      isLoadingOverrides,
      refetchOverrides,
      refreshQuote,
      deleteOverride,
      pendingResets,
      markForReset,
      resetPreviews,
      isLoadingResetPreviews,
    }),
    [
      isEditing,
      bannerState,
      startEditing,
      overrides,
      hasActiveOverrides,
      activeOverrides,
      activeOverridesByProduct,
      isLoadingOverrides,
      refetchOverrides,
      cancelEditing,
      completedEditing,
      notifiedCustomer,
      dismissBanner,
      setOverride,
      refreshQuote,
      deleteOverride,
      pendingResets,
      markForReset,
      resetPreviews,
      isLoadingResetPreviews,
    ]
  );

  useEffect(() => {
    onEditing?.(isEditing);
  }, [isEditing, onEditing]);

  useEffect(() => {
    if (bannerState !== 'notified') return;
    const timeout = setTimeout(() => setBannerState('hidden'), 5000);
    return () => clearTimeout(timeout);
  }, [bannerState]);

  const isEditingRef = useRef(isEditing);
  isEditingRef.current = isEditing;
  useEffect(() => {
    return () => {
      if (isEditingRef.current) unlockMap();
    };
  }, [unlockMap]);

  useOnCloseTab(
    useCallback(() => {
      if (isEditing) unlockMap();
    }, [isEditing, unlockMap])
  );

  return (
    <StaffPriceEditContext.Provider value={value}>
      {children}
    </StaffPriceEditContext.Provider>
  );
}
